import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { CardComponent } from "@shared/components/card/card.component";
import { CarouselComponent } from "@shared/components/carousel/carousel.component";
import { CarouselItemDirective } from "@shared/components/carousel/carousel-item.directive";
import { VideoModalComponent } from "@shared/components/modals/video-modal/video-modal.component";
import { PulseSurveyService } from "@shared/services/legacy/pulse-survey.service";
import { ModalService } from "@shared/services/modal/modal.service";
import { from, map, of, switchMap } from "rxjs";
import { CompanyService } from "src/services/company.service";
import { PleazService, Video } from "src/services/pleaz/pleaz.service";
import { ReportCardComponent } from "../../../shared/components/report-card/report-card.component";
import { ImagePipe } from "../../../shared/pipes/image/image.pipe";

@Component({
	selector: "app-pulse-survey-details",
	standalone: true,
	imports: [
		TranslateModule,
		CommonModule,
		RouterModule,
		ImagePipe,
		CardComponent,
		CarouselComponent,
		CarouselItemDirective,
		ReportCardComponent,
	],
	templateUrl: "./pulse-survey-details.component.html",
	styleUrls: ["./pulse-survey-details.component.scss"],
})
export class PulseSurveyDetailsComponent {
	readonly #route = inject(ActivatedRoute);
	readonly #router = inject(Router);
	readonly #pulseSurveyService = inject(PulseSurveyService);
	readonly #modalService = inject(ModalService);
	readonly #pleazService = inject(PleazService);
	readonly #companyService = inject(CompanyService);

	private readonly surveyId$ = this.#route.paramMap.pipe(
		map((params) => params.get("surveyId")),
	);

	public hasCounsellingAccess = signal(
		!!this.#companyService.company.HealthPartner,
	);

	readonly pulseSurveyId = toSignal(this.surveyId$);

	readonly pulseSurvey = toSignal(
		this.surveyId$.pipe(
			switchMap((id) =>
				id ? this.#pulseSurveyService.getHistoricPulseSurvey(id) : of(null),
			),
		),
		{ initialValue: null },
	);

	readonly score = computed(() => {
		const survey = this.pulseSurvey();
		if (!survey?.scoring) return null;
		return survey.scoring.find(
			(e: any) =>
				e.startScore <= survey.Value.Value && survey.Value.Value <= e.endScore,
		);
	});

	readonly scoreSize = computed(() => {
		const survey = this.pulseSurvey();
		return survey?.scoring?.length ? 100 / survey.scoring.length : 0;
	});

	readonly services = toSignal(
		this.surveyId$.pipe(
			switchMap((id) =>
				id ? this.#pulseSurveyService.getServices(id) : of([]),
			),
		),
		{ initialValue: [] },
	);

	readonly historicPulseSurveys = toSignal(
		this.surveyId$.pipe(
			switchMap((id) =>
				id
					? this.#pulseSurveyService.getHistoricPulseSurveysByType(id)
					: of([]),
			),
		),
		{ initialValue: [] },
	);

	readonly videos = computed(() => {
		const score = this.score();
		const videoIds = score?.videoIds ?? [];
		return videoIds.length
			? from(this.#pleazService.getFilteredVideos(videoIds))
			: of([]);
	});

	readonly isUserScore = (startScore: number, endScore: number): boolean => {
		const survey = this.pulseSurvey();
		return survey
			? startScore <= survey.Value.Value && survey.Value.Value <= endScore
			: false;
	};

	constructor() {
		this.loadFavorites();
	}

	public openVideo(video: Video): void {
		this.#modalService.openModal(VideoModalComponent, undefined, {
			video,
		});
	}

	readonly favoriteIds = signal<number[]>([]);

	private async loadFavorites() {
		try {
			const favorites = await this.#pleazService.getUserFavoriteVideos();
			this.favoriteIds.set(favorites.split(",").map((id) => Number(id)) ?? []);
		} catch (_error) {
			this.favoriteIds.set([]);
		}
	}

	public isFavoriteVideo(videoId: number): boolean {
		return this.favoriteIds().includes(videoId);
	}

	public async toggleFavoriteVideo(videoId: number): Promise<void> {
		if (this.isFavoriteVideo(videoId)) {
			this.#pleazService.removeUserFavoriteVideos({
				VideoId: videoId,
				UserId: 1,
			});
		} else {
			this.#pleazService.saveUserFavoriteVideos({
				VideoId: videoId,
				UserId: 1,
			});
		}
		await this.loadFavorites();
	}
	readonly viewReport = (surveyId: string): void => {
		this.#router.navigate(["/pulse-survey", surveyId, "history"]);
	};
}
